import { Link } from 'react-router-dom';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-[#1E2328]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to="/" className="inline-flex items-center text-[#3aa0e0] hover:text-[#3aa0e0] mb-8 transition-colors">
          ← Back to Home
        </Link>
        
        <h1 className="text-4xl font-bold text-white mb-8">Terms and Conditions</h1>
        
        <div className="prose prose-slate max-w-none">
          <p className="text-slate-400 mb-6">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              By downloading, installing, or using the FlyFinder mobile application ("App"), you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use the App.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">2. Description of Service</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              FlyFinder provides AI-powered fly fishing recommendations based on location, weather conditions, and regional hatch data. The App is designed for Canadian anglers and focuses on Canadian fishing conditions.
            </p>
            <p className="text-slate-300 leading-relaxed mb-4">
              Our service includes:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li>Location-based fly recommendations using GPS technology</li>
              <li>Real-time weather analysis optimized for Canadian conditions</li>
              <li>Comprehensive database of insects and fly patterns</li>
              <li>Digital logbook to track catches and patterns</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">3. User Accounts</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              To access certain features of the App, you may be required to create an account. You are responsible for:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use</li>
              <li>Providing accurate and complete information during registration</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">4. Subscription Plans</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              FlyFinder offers both Free and Pro subscription plans:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li><strong>Free Plan:</strong> Limited daily recommendations with basic features</li>
              <li><strong>Pro Plan:</strong> $3.99/month with unlimited access to all features</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mb-4">
              Subscriptions will automatically renew unless cancelled before the renewal date. You can cancel your subscription at any time through your account settings or the App Store/Google Play Store.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">5. Location Services</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              The App uses GPS location services to provide accurate fly recommendations. By using the App, you consent to the collection and use of your location data as described in our Privacy Policy. You can disable location services at any time through your device settings, but this may limit the functionality of the App.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">6. Acceptable Use</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li>Use the App for any illegal or unauthorized purpose</li>
              <li>Attempt to gain unauthorized access to any portion of the App</li>
              <li>Interfere with or disrupt the App or servers</li>
              <li>Copy, modify, or distribute any part of the App without permission</li>
              <li>Use automated systems to access the App</li>
              <li>Share or resell your account access</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">7. Intellectual Property</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              All content, features, and functionality of the App, including but not limited to text, graphics, logos, icons, images, data compilations, and software, are the property of FlyFinder and are protected by Canadian and international copyright, trademark, and other intellectual property laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">8. Disclaimer of Warranties</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. FlyFinder does not guarantee that:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li>The App will meet your specific requirements</li>
              <li>The App will be uninterrupted, timely, secure, or error-free</li>
              <li>The results obtained from using the App will be accurate or reliable</li>
              <li>Any recommendations will result in successful fishing</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mb-4">
              Fly fishing success depends on many factors beyond our control, including weather, water conditions, fishing skill, and local regulations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">9. Limitation of Liability</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              To the maximum extent permitted by law, FlyFinder shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of the App.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">10. Fishing Regulations</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              You are solely responsible for complying with all applicable federal, provincial, and local fishing regulations, including:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-300 space-y-2">
              <li>Obtaining required fishing licenses</li>
              <li>Following catch limits and size restrictions</li>
              <li>Respecting closed seasons and protected areas</li>
              <li>Using legal fishing methods and equipment</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mb-4">
              FlyFinder does not provide legal advice regarding fishing regulations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">11. Privacy</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Your use of the App is also governed by our Privacy Policy. Please review our Privacy Policy to understand our practices regarding your personal information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">12. Changes to Terms</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We reserve the right to modify these Terms and Conditions at any time. We will notify users of any material changes by posting the new Terms and Conditions on this page and updating the "Last updated" date. Your continued use of the App after any changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">13. Termination</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We may terminate or suspend your account and access to the App immediately, without prior notice or liability, for any reason, including if you breach these Terms and Conditions. Upon termination, your right to use the App will immediately cease.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">14. Governing Law</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              These Terms and Conditions shall be governed by and construed in accordance with the laws of Canada and the province of Ontario, without regard to conflict of law provisions.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">15. Contact Information</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              If you have any questions about these Terms and Conditions, please contact us at:
            </p>
            <p className="text-slate-300 leading-relaxed">
              Email: <a href="mailto:info@flyfinder.ca" className="text-[#3aa0e0] hover:text-[#3aa0e0]">info@flyfinder.ca</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
